import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Globe,
  ArrowLeft,
  Users,
  MessageCircle,
  Lock,
  Unlock,
  Send,
  Loader2,
  AlertCircle,
  Languages,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const RegionalRoomsPage = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadRooms();
  }, []);

  useEffect(() => {
    if (roomId && rooms.length > 0) {
      const room = rooms.find(r => r.id === roomId);
      if (room) {
        setSelectedRoom(room);
        loadRoomMessages(roomId);
      }
    }
  }, [roomId, rooms]);

  const loadRooms = async () => {
    try {
      const res = await api.getRegionalRooms();
      setRooms(res.data.rooms);
    } catch (error) {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const loadRoomMessages = async (rid) => {
    // Placeholder - in real app would fetch messages from backend
    setMessages([
      { id: 1, user: 'Sarah M.', region: 'North America', content: 'Just validated my first idea using the framework from Module 2!', time: '2 hours ago' },
      { id: 2, user: 'Carlos R.', region: 'Latin America', content: 'Congrats Sarah! What industry?', time: '1 hour ago' },
      { id: 3, user: 'Sarah M.', region: 'North America', content: 'EdTech - a tutoring marketplace for underserved communities.', time: '45 min ago' },
    ]);
  };

  const handleSelectRoom = (room) => {
    if (!room.has_access) {
      toast.error('Upgrade to Basic to access Regional Rooms');
      return;
    }
    navigate(`/regional-rooms/${room.id}`);
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedRoom?.can_post) return;
    
    setSending(true);
    // Placeholder - would send to backend
    setMessages(prev => [...prev, {
      id: Date.now(),
      user: user?.name || 'You',
      region: selectedRoom?.name,
      content: input.trim(),
      time: 'Just now'
    }]);
    setInput('');
    setSending(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  // Room detail view
  if (selectedRoom) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-ocean-blue text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    setSelectedRoom(null);
                    navigate('/regional-rooms');
                  }}
                  className="text-white hover:bg-white/10"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="font-bold flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    {selectedRoom.name}
                  </h1>
                  <p className="text-xs text-white/70">
                    {selectedRoom.is_home_region ? 'Your home region' : 'Viewing'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedRoom.auto_translate && (
                  <Badge variant="secondary" className="bg-white/20 text-white border-0">
                    <Languages className="w-3 h-3 mr-1" />
                    Auto-translate ON
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {/* Banner for non-home regions */}
          {!selectedRoom.is_home_region && selectedRoom.id !== 'general' && (
            <div className="bg-coral/20 border-t border-white/10 px-4 py-2">
              <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>You're viewing {selectedRoom.name} discussions. Join your region's room to participate.</span>
              </div>
            </div>
          )}
          
          <div className="wave-divider h-6" />
        </header>

        {/* Messages */}
        <main className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-ocean-blue/10 flex items-center justify-center">
                    <Users className="w-4 h-4 text-ocean-blue" />
                  </div>
                  <div>
                    <span className="font-medium text-sm">{msg.user}</span>
                    <span className="text-xs text-muted-foreground ml-2">{msg.region}</span>
                  </div>
                  <span className="text-xs text-muted-foreground ml-auto">{msg.time}</span>
                </div>
                <p className="text-foreground pl-10">{msg.content}</p>
              </motion.div>
            ))}
          </div>
        </main>

        {/* Input */}
        <footer className="sticky bottom-0 bg-background border-t border-border p-4">
          <div className="max-w-4xl mx-auto">
            {selectedRoom.can_post ? (
              <div className="flex items-center gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Share with the community..."
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  className="flex-1 rounded-xl"
                />
                <Button 
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  className="rounded-xl gradient-ocean text-white"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            ) : (
              <div className="bg-muted rounded-xl p-3 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Lock className="w-4 h-4" />
                <span>Join your home region to post messages</span>
              </div>
            )}
          </div>
        </footer>
      </div>
    );
  }

  // Room list view
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-ocean-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Regional Rooms
                </h1>
                <p className="text-xs text-white/70">Connect with founders worldwide</p>
              </div>
            </div>
          </div>
        </div>
        <div className="wave-divider h-6" />
      </header>

      {/* Room Grid */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room, index) => (
            <motion.div
              key={room.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                className={`rounded-2xl border-0 shadow-lg cursor-pointer transition-all hover:shadow-xl ${
                  room.is_home_region ? 'ring-2 ring-ocean-blue' : ''
                } ${!room.has_access ? 'opacity-60' : ''}`}
                onClick={() => handleSelectRoom(room)}
                data-testid={`room-${room.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${room.id === 'general' ? 'bg-mint' : 'bg-ocean-blue/10'} flex items-center justify-center`}>
                      {room.id === 'general' ? (
                        <Globe className="w-6 h-6 text-white" />
                      ) : (
                        <MapPin className="w-6 h-6 text-ocean-blue" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {room.is_home_region && (
                        <Badge className="bg-ocean-blue text-white">Home</Badge>
                      )}
                      {room.has_access ? (
                        <Unlock className="w-4 h-4 text-mint" />
                      ) : (
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{room.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{room.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Active
                    </span>
                    {room.can_post ? (
                      <span className="flex items-center gap-1 text-mint">
                        <MessageCircle className="w-3 h-3" />
                        Can post
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        View only
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default RegionalRoomsPage;
